import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  DefaultColorStyle,
  type Editor,
  TLComponents,
  Tldraw,
  TldrawOverlays,
  TLUiOverrides,
} from "tldraw";
import "./canvas.css";
import { CANVAS_OPTIONS, CANVAS_UI_OVERRIDES } from "./canvasOptions";
import type { TldrawCanvasAgentProps } from "./TldrawCanvasAgentProps";
import { TldrawAgentApp } from "./agent/TldrawAgentApp";
import {
  TldrawAgentAppContextProvider,
  TldrawAgentAppProvider,
} from "./agent/TldrawAgentAppProvider";
import { AgentHelpers } from "./AgentHelpers";
import { CustomHelperButtons } from "./components/CustomHelperButtons";
import {
  CanvasPaperBackground,
  PAGE_HEIGHT,
  PAGE_WIDTH,
} from "./components/CanvasPaperBackground";
import { CanvasToolbar } from "./components/CanvasToolbar";
import { CanvasOutOfViewTracker } from "./components/CanvasOutOfViewTracker";
import { registerStudentShapeProtection } from "./utils/studentShapeProtection";

import { AllAgentCursors } from "./components/cursor/AgentCursorOverlay";
import { AllAgentGestures } from "./components/highlights/GestureMarks";
import { ET_CANVAS_ASSET_URLS } from "@ever-tutor/et-ui";
import { TLDRAW_LICENSE_KEY } from "@/constants/tldraw";
import { NOTEPAD_SHAPE_UTILS } from "./ink/notepadShapeUtils";
import { TldrawDrawToolRegion } from "@/drawlock/TldrawDrawToolRegion";

const DESK_BACKGROUNDS = [
  "/canvas-desks/wood-desk-1.avif",
  "/canvas-desks/desk-holiday.avif",
  "/canvas-desks/desk-halloween.avif",
  "/canvas-desks/desk-space.avif",
  "/canvas-desks/desk-sports.avif",
  "/canvas-desks/desk-ocean.avif",
  "/canvas-desks/desk-nature.avif",
  "/canvas-desks/desk-robots.avif",
  "/canvas-desks/desk-birthday.avif",
  "/canvas-desks/desk-math.avif",
];

function buildOverrides(
  onCameraClickRef: React.MutableRefObject<(() => void) | undefined>,
): TLUiOverrides {
  return {
    ...CANVAS_UI_OVERRIDES,
    tools: (_editor, tools) => ({
      ...tools,
      // Override the built-in asset tool to open our camera/upload modal
      asset: {
        ...tools["asset"],
        onSelect() {
          onCameraClickRef.current?.();
        },
      },
    }),
  };
}

export function TldrawCanvasAgent({
  onMount: onMountProp,
  onUnmount: onUnmountProp,
  onStreamComplete,
  onError,
  onActionExecuted,
  onActionCrash,
  pageKey = null,
  onEditorMount,
  onBeforeUnmount,
  actionHistory,
  onSharePanelRef,
  blobScale = 1,
  onCameraClick,
}: TldrawCanvasAgentProps) {
  const deskBg = useMemo(
    () => DESK_BACKGROUNDS[Math.floor(Math.random() * DESK_BACKGROUNDS.length)],
    [],
  );
  const [mountedPageKey] = useState(pageKey);
  // A snapshot prop can rebuild tldraw; the panel loads once at editor mount.
  const editorRef = useRef<Editor | null>(null);
  const onEditorMountRef = useRef(onEditorMount);
  onEditorMountRef.current = onEditorMount;
  const onBeforeUnmountRef = useRef(onBeforeUnmount);
  onBeforeUnmountRef.current = onBeforeUnmount;

  // Runs before tldraw disposes the editor and a replacement loads its board.
  useLayoutEffect(() => {
    return () => {
      const editor = editorRef.current;
      if (editor) onBeforeUnmountRef.current?.(mountedPageKey, editor);
    };
  }, [mountedPageKey]);

  const [app, setApp] = useState<TldrawAgentApp | null>(null);
  const appRef = useRef(app);
  appRef.current = app;
  const actionHistoryRef = useRef(actionHistory);
  actionHistoryRef.current = actionHistory;
  const onSharePanelRefRef = useRef(onSharePanelRef);
  onSharePanelRefRef.current = onSharePanelRef;
  const onCameraClickRef = useRef(onCameraClick);
  onCameraClickRef.current = onCameraClick;

  const handleMount = useCallback(
    (instance: TldrawAgentApp) => {
      setApp(instance);
      onMountProp?.(instance);
    },
    [onMountProp],
  );

  const handleUnmount = useCallback(() => {
    setApp(null);
    onUnmountProp?.();
  }, [onUnmountProp]);

  useEffect(() => {
    if (
      !app ||
      !actionHistoryRef.current ||
      actionHistoryRef.current.length === 0
    )
      return;
    const agent = app.agents.getAgent();
    if (!agent) return;

    const actions = actionHistoryRef.current;
    const helpers = new AgentHelpers(agent);
    agent.setIsActingOnEditor(true);
    try {
      agent.editor.run(
        () => {
          for (const action of actions) {
            const actionUtil = agent.actions.getAgentActionUtil(action._type);
            const transformed = actionUtil.sanitizeAction(action, helpers);
            if (transformed) {
              agent.actions.act(transformed, helpers);
            }
          }
        },
        { ignoreShapeLock: true, history: "ignore" },
      );
    } finally {
      agent.setIsActingOnEditor(false);
    }
  }, [app]);

  const components: TLComponents = useMemo(
    () => ({
      HelperButtons: () =>
        app && (
          <TldrawAgentAppContextProvider app={app}>
            <CustomHelperButtons />
          </TldrawAgentAppContextProvider>
        ),
      TopPanel: () =>
        app ? (
          <TldrawAgentAppContextProvider app={app}>
            <CanvasOutOfViewTracker />
          </TldrawAgentAppContextProvider>
        ) : null,
      Overlays: () => (
        <>
          <TldrawOverlays />
          {app && (
            <TldrawAgentAppContextProvider app={app}>
              {/* Transient tutor gestures — an overlay, never a shape. The
                  Overlays slot is the whole mechanism: see GestureActionUtil. */}
              <AllAgentGestures />
              <AllAgentCursors />
            </TldrawAgentAppContextProvider>
          )}
        </>
      ),
      SharePanel: () => (
        <div
          ref={(el) => onSharePanelRefRef.current?.(el)}
          style={{
            width: 260 * blobScale,
            height: 140 * blobScale,
            margin: "10px",
            marginBottom: 0,
            flexShrink: 0,
          }}
        />
      ),
      MainMenu: null,
      PageMenu: null,
      ActionsMenu: null,
      QuickActions: null,
      Minimap: null,
      NavigationPanel: null,
      StylePanel: null,
      OnTheCanvas: CanvasPaperBackground,
      Toolbar: CanvasToolbar,
    }),
    [app, blobScale],
  );

  const uiOverrides = useMemo(() => buildOverrides(onCameraClickRef), []);

  return (
    <div
      className="tldraw-canvas tldraw-canvas-agent"
      style={{ "--desk-bg": `url(${deskBg})` } as React.CSSProperties}
    >
      <Tldraw
        licenseKey={TLDRAW_LICENSE_KEY}
        assetUrls={ET_CANVAS_ASSET_URLS}
        shapeUtils={NOTEPAD_SHAPE_UTILS}
        options={CANVAS_OPTIONS}
        overrides={uiOverrides}
        components={components}
        onMount={(e) => {
          editorRef.current = e;
          // First, so the camera and tool setup below win over the board's.
          onEditorMountRef.current?.(mountedPageKey, e);
          // tldraw defaults to black, which PEN_COLORS only hides from the picker.
          e.setStyleForNextShapes(DefaultColorStyle, "blue");
          // A student comes here to write, so the pen is in hand from the
          // start; select is one toolbar tap away.
          e.setCurrentTool("draw");
          // Ink size and dash defaults are app-wide, set in canvasOptions.ts.
          e.setCameraOptions({ zoomSteps: [1], constraints: undefined });
          e.setCamera({ x: 0, y: 0, z: 1 }, { force: true });

          // Elastic rubber-band overscroll: a per-tick spring pulls the camera
          // back into the paper bounds, revealing the desk only at the edges.
          const cleanupSpring = (() => {
            const onTick = () => {
              const cam = e.getCamera();
              const vsb = e.getViewportScreenBounds();
              const z = cam.z;
              const upperX = 0;
              const upperY = 0;
              const lowerX = Math.min(0, vsb.w / z - PAGE_WIDTH);
              const lowerY = Math.min(0, vsb.h / z - PAGE_HEIGHT);
              const down = e.inputs.getIsPointing();
              const buffer = 100 / z;
              let { x, y } = cam;
              let changed = false;
              const springAxis = (
                v: number,
                lo: number,
                hi: number,
              ): number => {
                const capped = Math.max(lo - buffer, Math.min(hi + buffer, v));
                // While pointing, only cap — springing back fights tldraw's pan.
                if (down) {
                  if (capped !== v) changed = true;
                  return capped;
                }
                let target = capped;
                if (capped < lo) target = lo;
                else if (capped > hi) target = hi;
                if (target === capped) return capped;
                let next = capped + (target - capped) * 0.55;
                if (Math.abs(target - next) < 0.5) next = target;
                changed = true;
                return next;
              };
              x = springAxis(x, lowerX, upperX);
              y = springAxis(y, lowerY, upperY);
              if (changed) e.setCamera({ x, y, z }, { force: true });
            };
            e.on("tick", onTick);
            return () => e.off("tick", onTick);
          })();
          const cleanupShapeProtection = registerStudentShapeProtection(
            e,
            () =>
              appRef.current?.agents.getAgent()?.getIsActingOnEditor() ?? false,
          );

          return () => {
            cleanupSpring();
            cleanupShapeProtection();
          };
        }}
        // No persistenceKey: the panel owns the saved board (SESS-01).
      >
        <TldrawAgentAppProvider
          onMount={handleMount}
          onUnmount={handleUnmount}
          persist={false}
          onStreamComplete={onStreamComplete}
          onError={onError}
          onActionExecuted={onActionExecuted}
          onActionCrash={onActionCrash}
        />
        <TldrawDrawToolRegion />
      </Tldraw>
    </div>
  );
}
